import {
  type BusinessCategory,
  type BusinessType,
  type CanonicalBusinessTag,
  toCanonicalBusinessTags,
} from "@/web/lib/schemaUtils/businessCategories";
import type {
  FirstMatchingNominationRow,
  InvitedOnboardingStep,
  SmbOnboardingFlowKind,
  SmbOnboardingRow,
} from "@tycho/web-bindings/types";

// ---------------------------------------------------------------------------
// Local types (SMB onboarding view row → wizard document shape)
// ---------------------------------------------------------------------------

export type AddressParts = {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  countryCode: string;
};

/** Map generated `SmbOnboardingRow` (public `smb_onboarding` view) into the wizard document shape used by Ploutos-era steps. */
export function smbOnboardingFromViewRow(rawRow: SmbOnboardingRow): SmbOnboarding {
  return {
    googlePlaceId: rawRow.googlePlaceId ?? null,
    name: rawRow.name ?? null,
    addressParts: rawRow.businessAddress
      ? {
          addressLine1: rawRow.businessAddress.addressLine1,
          addressLine2: rawRow.businessAddress.addressLine2 ?? undefined,
          city: rawRow.businessAddress.city,
          state: rawRow.businessAddress.state,
          zip: rawRow.businessAddress.zip,
          countryCode: rawRow.businessAddress.countryCode,
        }
      : null,
    businessCategory: (rawRow.businessCategory as BusinessCategory) ?? null,
    businessType: (rawRow.businessType as BusinessType) ?? null,
    websiteUrl: rawRow.websiteUrl ?? null,
    nominatedBusinessId: rawRow.nominatedBusinessV1 ?? null,
    claimRequestId: rawRow.claimRequestId ?? null,
    heroCdnPath: rawRow.heroCdnPath ?? null,
    colors: rawRow.colorPrimary
      ? {
          primary: rawRow.colorPrimary,
          secondary: rawRow.colorSecondary ?? "#444444",
          tertiary: rawRow.colorTertiary ?? "#999999",
          text: rawRow.colorText ?? "#ffffff",
        }
      : null,
    // The DB may still hold legacy slugs from brands saved before the tag
    // refactor. Map them to their canonical equivalents so they re-hydrate as
    // *selected* in the picker — otherwise a re-edit + continue would silently
    // clear those tags. Unmappable legacy slugs are dropped (accepted loss).
    businessTags: toCanonicalBusinessTags(rawRow.businessTags ?? []),
    browsable: rawRow.browsable,
    flowKind: rawRow.flowKind,
    sourceInvitedBrandDraft: rawRow.sourceInvitedBrandDraft?.toString() ?? null,
    invitedStep: rawRow.invitedStep ?? null,
  };
}

export type SmbOnboarding = {
  googlePlaceId?: string | null;
  name?: string | null;
  addressParts?: AddressParts | null;
  businessCategory?: BusinessCategory | null;
  businessType?: BusinessType | null;
  websiteUrl?: string | null;
  nominatedBusinessId?: string | null;
  claimRequestId?: string | null;
  heroCdnPath?: string | null;
  colors?: { primary: string; secondary: string; tertiary: string; text: string } | null;
  businessTags?: CanonicalBusinessTag[] | null;
  /** Whether the card minted at onboarding completion should show on `/browse`. Defaults to `true`. */
  browsable?: boolean | null;
  /** Invited-source metadata (present only on invited drafts; `undefined`/null on organic). */
  flowKind?: SmbOnboardingFlowKind | null;
  sourceInvitedBrandDraft?: string | null;
  invitedStep?: InvitedOnboardingStep | null;
};

// ---------------------------------------------------------------------------
// Step union
// ---------------------------------------------------------------------------

export type SmbOnboardingStep =
  | "identifyBusiness"
  | "confirmBusiness"
  | "claimNomination"
  | "customizeStyling"
  | "supplementalInfo"
  | "userInfo";

// ---------------------------------------------------------------------------
// Step helpers (ported from shared/smbOnboarding/helpers.ts)
// ---------------------------------------------------------------------------

/** Resumes the SMB wizard from persisted row state (users with brands never hit this route). */
export function getSmbOnboardingStep(doc: SmbOnboarding): SmbOnboardingStep {
  if (!doc.name || !doc.addressParts || !doc.businessCategory || !doc.businessType)
    return "identifyBusiness";
  if (!doc.websiteUrl) return "confirmBusiness";
  if (doc.nominatedBusinessId && !doc.claimRequestId) return "claimNomination";
  if (!doc.heroCdnPath || !doc.colors) return "customizeStyling";
  if (!doc.businessTags?.length) return "supplementalInfo";
  return "userInfo";
}

/** True when the onboarding draft is an invited (acquisition) draft. */
export function isInvitedOnboarding(doc: SmbOnboarding | null | undefined): boolean {
  return doc?.flowKind?.tag === "Invited";
}

/**
 * Map a persisted invited step to the wizard step it should resume at. Invited
 * drafts start at card customization and skip Identify Business, Confirm
 * Details, and nomination-claim entirely. Pre-filled fields cannot derive this
 * step, so we always honor the persisted `invitedStep`.
 */
export function invitedStepToWizardStep(
  invitedStep: InvitedOnboardingStep | null | undefined,
): SmbOnboardingStep {
  switch (invitedStep?.tag) {
    case "Supplemental":
      return "supplementalInfo";
    case "UserInfo":
      return "userInfo";
    default:
      return "customizeStyling";
  }
}

/** Previous wizard step when the stack is only one frame deep (e.g. deep-link / refresh). */
export function smbBackTarget(step: SmbOnboardingStep, doc: SmbOnboarding): SmbOnboardingStep {
  if (isInvitedOnboarding(doc)) {
    // Invited flow has no Identify/Confirm/Claim steps — back only moves among
    // Customize → Supplemental → User Info. First frame is a no-op (stay).
    switch (step) {
      case "supplementalInfo":
        return "customizeStyling";
      case "userInfo":
        return "supplementalInfo";
      default:
        return "customizeStyling";
    }
  }
  switch (step) {
    case "confirmBusiness":
      return "identifyBusiness";
    case "claimNomination":
      return "confirmBusiness";
    case "customizeStyling":
      return doc.nominatedBusinessId ? "claimNomination" : "confirmBusiness";
    case "supplementalInfo":
      return "customizeStyling";
    case "userInfo":
      return "supplementalInfo";
    default:
      return "identifyBusiness";
  }
}

export function isBusinessDetailsComplete(doc: SmbOnboarding): boolean {
  return Boolean(doc.name && doc.addressParts && doc.businessType && doc.businessCategory);
}

/** Client-side next step after saving business details (mirrors `getSmbOnboardingStep` claim branch; uses `first_matching_nomination` view). */
export function nextStepAfterConfirmBusinessDetails(
  nomination: FirstMatchingNominationRow | null,
): "claimNomination" | "customizeStyling" {
  if (nomination !== null && !nomination.submittedClaimRequest) return "claimNomination";
  return "customizeStyling";
}

/**
 * Builds the canonical business address object expected by SpacetimeDB reducer args
 * (`canonical_address`) from an `AddressParts` object.
 */
export function toCanonicalAddress(a: AddressParts) {
  return {
    addressLine1: a.addressLine1,
    addressLine2: a.addressLine2,
    city: a.city,
    state: a.state,
    zip: a.zip,
    countryCode: a.countryCode,
  };
}
